import { useState, useEffect } from "react";
import { startOfWeek, startOfMonth, endOfMonth, format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { parseScheduleData, parseClosurePeriodsData } from "@/utils/courseUtils";
import { CalendarEvent, CourseWithSchedule } from "./types";
import { generateCourseEventId } from "./event-mappers";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";

// Mappa dei nomi dei giorni in italiano ai numeri dei giorni (1 = lunedì, 7 = domenica)
const ITALIAN_DAY_MAP: { [key: string]: number } = {
  "Lunedì": 1,
  "Martedì": 2,
  "Mercoledì": 3,
  "Giovedì": 4,
  "Venerdì": 5,
  "Sabato": 6,
  "Domenica": 7
};

// Mappa dei nomi dei giorni in inglese ai numeri dei giorni
const ENGLISH_DAY_MAP: { [key: string]: number } = {
  "monday": 1, "Monday": 1,
  "tuesday": 2, "Tuesday": 2,
  "wednesday": 3, "Wednesday": 3,
  "thursday": 4, "Thursday": 4,
  "friday": 5, "Friday": 5,
  "saturday": 6, "Saturday": 6,
  "sunday": 7, "Sunday": 7
};

export const useCourseEvents = (
  currentDate: Date, 
  isClientView: boolean, 
  clientId: string | null,
  setError: (error: Error | null) => void,
  toast: any
) => {
  // Creiamo una chiave univoca per la cache che non dipende dal mese
  const cacheKey = isClientView 
    ? `course_events_client_${clientId}`
    : 'course_events_admin';

  // Inizializziamo un valore iniziale vuoto per evitare referenze circolari
  const initialEvents: CalendarEvent[] = [];
  
  // Utilizziamo useGlobalCache per memorizzare gli eventi dei corsi
  const { 
    data: courseEvents = initialEvents, 
    isLoading 
  } = useGlobalCache<CalendarEvent[]>(
    cacheKey,
    async () => {
      try {
        // Costruzione della query per i corsi
        let coursesQuery;
        
        if (isClientView && clientId) {
          const { data: clientCoursesData, error: clientCoursesError } = await supabase
            .from("client_courses")
            .select("course_id")
            .eq("client_id", clientId);
            
          if (clientCoursesError) {
            setError(clientCoursesError);
            throw clientCoursesError;
          }
          
          const courseIds = clientCoursesData?.map(cc => cc.course_id) || [];
          
          if (courseIds.length === 0) {
            return [];
          }

          coursesQuery = supabase
            .from("courses")
            .select("id, name, schedule, closure_periods, color_code, add_to_calendar, start_date, end_date")
            .eq("add_to_calendar", true)
            .in("id", courseIds);
        } else {
          coursesQuery = supabase
            .from("courses")
            .select("id, name, schedule, closure_periods, color_code, add_to_calendar, start_date, end_date")
            .eq("add_to_calendar", true);
        }
        
        const { data: coursesData, error: coursesError } = await coursesQuery;
        
        if (coursesError) {
          console.error("Errore nel caricamento dei corsi:", coursesError);
          setError(coursesError);
          toast({
            title: "Errore",
            description: "Impossibile caricare i corsi per il calendario",
            variant: "destructive"
          });
          return [];
        }

        // Create a map of course colors
        const courseColorMap = new Map<string, string>();
        if (coursesData) {
          coursesData.forEach(course => {
            if (course && course.id && course.color_code) {
              courseColorMap.set(course.id, course.color_code);
            }
          });
        }

        const processedCourses = (coursesData || []).map(course => {
          return {
            ...course,
            schedule: parseScheduleData(course.schedule),
            closure_periods: parseClosurePeriodsData(course.closure_periods)
          };
        });
        
        // Generazione degli eventi dei corsi
        const events = generateEventsFromCourses(processedCourses, currentDate, courseColorMap);
        return events;
      } catch (error) {
        console.error("Errore nel caricamento degli eventi dei corsi:", error);
        setError(error instanceof Error ? error : new Error('Errore sconosciuto'));
        return [];
      }
    },
    {
      validity: 24 * 60 * 60 * 1000,     // 24 ore (aumentato)
      staleTime: 24 * 60 * 60 * 1000,    // 24 ore (aumentato)
      setupRealtimeSubscription: (notifyUpdate) => {
        return createSupabaseSubscription(
          supabase,
          'courses',
          {},
          notifyUpdate
        );
      }
    }
  );

  return {
    courseEvents: courseEvents || [], // Assicuriamo che sia sempre un array
    isLoadingCourses: isLoading
  };
};

// Funzione per generare gli eventi dai corsi
export const generateEventsFromCourses = (
  courses: CourseWithSchedule[], 
  currentDate: Date, 
  courseColorMap: Map<string, string>
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  
  // Ottieni la data di inizio settimana corrente per il calendario
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  
  courses.forEach(course => {
    console.log("Elaborazione corso per calendario:", course.name, "Schedule:", course.schedule);
    
    if (!course.schedule || course.schedule.length === 0) {
      console.log("Nessuno schedule valido trovato per il corso:", course.name);
      return;
    }
    
    // Utilizziamo direttamente l'array di schedule che è già stato processato
    const scheduleArray = course.schedule;
    
    // Determina il range di date per cui generare gli eventi
    // Utilizziamo un periodo ampio per assicurarci di coprire sia date passate che future
    const today = new Date();
    
    // Per default, genera eventi per 2 mesi prima e 2 mesi dopo la data corrente
    let startRange = startOfMonth(new Date(today));
    startRange.setMonth(startRange.getMonth() - 2);
    
    let endRange = endOfMonth(new Date(today));
    endRange.setMonth(endRange.getMonth() + 2);
    
    // Se il corso ha date di inizio/fine specifiche, rispettale
    if (course.start_date) {
      const courseStartDate = new Date(course.start_date);
      if (courseStartDate > startRange) {
        startRange = courseStartDate;
      }
    }
    
    if (course.end_date) {
      const courseEndDate = new Date(course.end_date);
      if (courseEndDate < endRange) {
        endRange = courseEndDate;
      }
    }
    
    // Genera eventi per ogni giorno nel range di date
    const currentDayIteration = new Date(startRange);
    
    while (currentDayIteration <= endRange) {
      const currentDayOfWeek = currentDayIteration.getDay() || 7; // Trasforma 0 (domenica) in 7
      
      scheduleArray.forEach((scheduleItem: any) => {
        // Ottieni il numero del giorno della settimana per questo slot di schedule
        let scheduleDayNum = 0;
        
        if (typeof scheduleItem.day === 'string') {
          // Prova prima con la mappa italiana
          scheduleDayNum = ITALIAN_DAY_MAP[scheduleItem.day];
          
          // Se non trovato, prova a usare direttamente il nome del giorno in inglese
          if (!scheduleDayNum) {
            scheduleDayNum = ENGLISH_DAY_MAP[scheduleItem.day.toLowerCase()];
          }
        }
        
        // Se il giorno corrente corrisponde a uno schedulato
        if (currentDayOfWeek === scheduleDayNum) {
          // Estrai l'ora dallo schedule
          let hour = 0;
          let minute = 0;
          
          if (scheduleItem.time) {
            const [hourStr, minuteStr] = scheduleItem.time.split(":");
            hour = parseInt(hourStr, 10);
            minute = parseInt(minuteStr, 10);
          } else if (scheduleItem.startTime) {
            const [hourStr, minuteStr] = scheduleItem.startTime.split(":");
            hour = parseInt(hourStr, 10);
            minute = parseInt(minuteStr, 10);
          }
          
          // Crea l'evento per questo giorno specifico
          const eventDate = new Date(currentDayIteration);
          eventDate.setHours(0, 0, 0, 0); // Resetta l'ora
          
          // Aggiungi l'evento alla lista
          events.push({
            id: generateCourseEventId(course.id, eventDate, hour, minute),
            title: course.name,
            date: eventDate,
            hour,
            minute,
            clientId: "",  // Aggiunto il campo clientId che era mancante
            courseId: course.id,
            type: "course",
            colorCode: courseColorMap.get(course.id)
          });
        }
      });
      
      // Passa al giorno successivo
      currentDayIteration.setDate(currentDayIteration.getDate() + 1);
    }
  });
  
  console.log("Eventi generati dai corsi:", events);
  return events;
};
